import React from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import useRLContext from "@/hooks/useRLContext";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * RLSectionGuard — SS-8/CP-1.3 (Jun 2026).
 *
 * Route-level defence-in-depth for the Racket Lab console: a page only
 * renders when the viewer's RBAC `sections` (from me-context) include
 * the required section. Direct-URL access to a non-allowed section
 * shows a neutral denied panel (no data leak — backend 403/404 remain
 * the real wall).
 *
 * `section` accepts a string or an array (any-of), e.g. the ticket
 * detail page is reachable from both `tickets` and `my_tickets`.
 */
export const RLSectionGuard = ({ section, children }) => {
  const t = useRacketLabAdminT();
  const navigate = useNavigate();
  const { ctx, loading } = useRLContext();

  if (loading) {
    return (
      <div data-testid="rl-section-guard-loading" className="p-8 text-center text-xs text-slate-400">
        …
      </div>
    );
  }

  const sections = ctx?.sections || [];
  const wanted = Array.isArray(section) ? section : [section];
  if (wanted.some((s) => sections.includes(s))) return children;

  return (
    <div
      data-testid="rl-section-denied"
      className="mx-auto mt-12 flex max-w-md flex-col items-center gap-3 rounded-lg border border-slate-200 bg-white p-8 text-center"
    >
      <ShieldAlert className="h-8 w-8 text-amber-500" />
      <h2 className="text-sm font-semibold text-slate-900">{t("guard.denied_title")}</h2>
      <p className="text-xs text-slate-500">{t("guard.denied_body")}</p>
      <button
        type="button"
        data-testid="rl-section-denied-back"
        onClick={() => navigate("/admin/racket-lab")}
        className="mt-2 rounded-full bg-emerald-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
      >
        {t("guard.back_to_dashboard")}
      </button>
    </div>
  );
};

export default RLSectionGuard;
